import React from 'react';
import './FilterBar.css';

export interface FilterState {
  curriculum: 'all' | 'music' | 'video-game-design' | 'movie-making';
  gameMode: 'all' | 'group' | 'solo' | 'both';
  tier: 'all' | 'premium' | 'basic';
  playerCount: 'all' | 'solo' | 'duo' | 'group' | 'large-group';
  musicalElements: string[];
  musicalSkills: string[];
  searchTerm: string;
  category?: string;
  elementCategory?: string;
}

interface FilterBarProps {
  filters: FilterState;
  onFilterChange: (filters: FilterState) => void;
  compact?: boolean;
}

const FilterBar: React.FC<FilterBarProps> = ({ filters, onFilterChange, compact }) => {
  const updateFilter = <K extends keyof FilterState>(
    key: K,
    value: FilterState[K]
  ) => {
    onFilterChange({ ...filters, [key]: value });
  };

  const selectCurriculum = (curriculum: FilterState['curriculum']) => {
    onFilterChange({
      ...filters,
      curriculum,
      ...(curriculum !== 'music' ? { category: 'all', elementCategory: 'all' } : {})
    });
  };

  const toggleArrayFilter = (
    key: 'musicalElements' | 'musicalSkills',
    value: string
  ) => {
    const currentArray = filters[key];
    const newArray = currentArray.includes(value)
      ? currentArray.filter(item => item !== value)
      : [...currentArray, value];
    updateFilter(key, newArray);
  };

  const clearAllFilters = () => {
    onFilterChange({
      curriculum: 'all',
      gameMode: 'all',
      tier: 'all',
      playerCount: 'all',
      musicalElements: [],
      musicalSkills: [],
      searchTerm: '',
      category: 'all',
      elementCategory: 'all'
    });
  };

  const hasActiveFilters = 
    filters.curriculum !== 'all' ||
    filters.gameMode !== 'all' ||
    filters.tier !== 'all' ||
    filters.playerCount !== 'all' ||
    filters.musicalElements.length > 0 ||
    filters.musicalSkills.length > 0 ||
    filters.searchTerm !== '' ||
    (filters.category && filters.category !== 'all') ||
    (filters.elementCategory && filters.elementCategory !== 'all');

  const musicalElements = [
    { key: 'harmony', icon: '🎵', label: 'Harmony' },
    { key: 'instruments', icon: '🎸', label: 'Instruments' },
    { key: 'melody', icon: '🎼', label: 'Melody' },
    { key: 'form', icon: '🏗️', label: 'Form' },
    { key: 'rhythm', icon: '🥁', label: 'Rhythm' },
    { key: 'dynamics', icon: '📢', label: 'Dynamics' }
  ];

  const musicalSkills = [
    { key: 'performance', icon: '🎭', label: 'Performance' },
    { key: 'improvisation', icon: '✨', label: 'Improvisation' },
    { key: 'composition', icon: '✍️', label: 'Composition' },
    { key: 'music theory', icon: '📚', label: 'Music Theory' }
  ];

  const categories = [
    { key: 'all', label: 'All Skills' },
    { key: 'performance', label: 'Performance' },
    { key: 'improvisation', label: 'Improvisation' },
    { key: 'composition', label: 'Composition' },
    { key: 'music theory', label: 'Music Theory' }
  ];

  const elementCategories = [
    { key: 'all', label: 'All Core Concepts' },
    { key: 'harmony', label: 'Harmony' },
    { key: 'rhythm', label: 'Rhythm' },
    { key: 'melody', label: 'Melody' },
    { key: 'instruments', label: 'Instruments' },
    { key: 'form', label: 'Form' },
    { key: 'dynamics', label: 'Dynamics' },
  ];

  if (compact) {
    return (
      <div className="filter-bar filter-bar--compact">
        <div className="curriculum-filter-group">
          <div className="filter-group-heading">
            <span className="filter-group-kicker">Browse by curriculum</span>
            <span className="filter-group-help">Real or AI appears in every curriculum.</span>
          </div>
          <div className="curriculum-pills" role="group" aria-label="Filter games by curriculum">
            <button
              className={`curriculum-pill ${filters.curriculum === 'all' ? 'active' : ''}`}
              onClick={() => selectCurriculum('all')}
              aria-pressed={filters.curriculum === 'all'}
            >
              <span className="curriculum-icon">🎮</span><span><strong>All games</strong><small>Browse everything</small></span>
            </button>
            <button
              className={`curriculum-pill ${filters.curriculum === 'music' ? 'active' : ''}`}
              onClick={() => selectCurriculum('music')}
              aria-pressed={filters.curriculum === 'music'}
            >
              <span className="curriculum-icon">🎵</span><span><strong>Music</strong><small>Music curriculum</small></span>
            </button>
            <button
              className={`curriculum-pill ${filters.curriculum === 'video-game-design' ? 'active' : ''}`}
              onClick={() => selectCurriculum('video-game-design')}
              aria-pressed={filters.curriculum === 'video-game-design'}
            >
              <span className="curriculum-icon">🕹️</span><span><strong>Video Game Design</strong><small>Game design curriculum</small></span>
            </button>
            <button
              className={`curriculum-pill ${filters.curriculum === 'movie-making' ? 'active' : ''}`}
              onClick={() => selectCurriculum('movie-making')}
              aria-pressed={filters.curriculum === 'movie-making'}
            >
              <span className="curriculum-icon">🎬</span><span><strong>Movie Making</strong><small>Filmmaking curriculum</small></span>
            </button>
          </div>
        </div>

        <div className="compact-controls compact-controls--secondary">
          <label className="filter-field filter-field--search">
            <span>Search</span>
            <input type="search" placeholder="Search by game name…" value={filters.searchTerm}
              onChange={e => updateFilter('searchTerm', e.target.value)} className="search-input" />
          </label>

          <label className="filter-field">
            <span>Players</span>
            <select
              value={filters.gameMode}
              onChange={e => updateFilter('gameMode', e.target.value as FilterState['gameMode'])}
              className="category-select"
            >
              <option value="all">Any number</option><option value="solo">Solo</option><option value="group">Group</option>
            </select>
          </label>

          {(filters.curriculum === 'music' || filters.curriculum === 'all') && <label className="filter-field">
            <span>Music skill</span>
            <select
              value={filters.category || 'all'} onChange={e => updateFilter('category', e.target.value)}
              className="category-select"
            >
              {categories.map(item => <option key={item.key} value={item.key}>{item.label}</option>)}
            </select>
          </label>}

          {(filters.curriculum === 'music' || filters.curriculum === 'all') && <label className="filter-field">
            <span>Music concept</span>
            <select value={filters.elementCategory || 'all'} onChange={e => updateFilter('elementCategory', e.target.value)} className="category-select">
              {elementCategories.map(item => <option key={item.key} value={item.key}>{item.label}</option>)}
            </select>
          </label>}

          {hasActiveFilters && <button onClick={clearAllFilters} className="clear-btn">Reset filters</button>}
        </div>

        {(filters.musicalElements.length > 0 || filters.musicalSkills.length > 0) && (
          <div className="active-filters-compact">
            {filters.musicalElements.map(element => {
              const elementData = musicalElements.find(e => e.key === element);
              return (
                <span 
                  key={element} 
                  className="active-pill element-pill"
                  onClick={() => toggleArrayFilter('musicalElements', element)}
                >
                  {elementData?.icon} {elementData?.label} ×
                </span>
              );
            })}
            {filters.musicalSkills.map(skill => {
              const skillData = musicalSkills.find(s => s.key === skill);
              return (
                <span 
                  key={skill} 
                  className="active-pill skill-pill"
                  onClick={() => toggleArrayFilter('musicalSkills', skill)}
                >
                  {skillData?.icon} {skillData?.label} ×
                </span>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="filter-bar">
      <div className="filter-header">
        <h2 className="filter-title">🎮 Game Filters</h2>
        {hasActiveFilters && (
          <button onClick={clearAllFilters} className="clear-all-btn">
            ✨ Clear All
          </button>
        )}
      </div>

      <div className="filter-grid">
        {/* Game Mode Section */}
        <div className="filter-section">
          <h3 className="section-title">👥 Players</h3>
          <div className="mode-buttons">
            <button
              className={`filter-btn ${filters.gameMode === 'all' ? 'active' : ''}`}
              onClick={() => updateFilter('gameMode', 'all')}
            >
              All
            </button>
            <button
              className={`filter-btn ${filters.gameMode === 'group' ? 'active' : ''}`}
              onClick={() => updateFilter('gameMode', 'group')}
            >
              👥 Group
            </button>
            <button
              className={`filter-btn ${filters.gameMode === 'solo' ? 'active' : ''}`}
              onClick={() => updateFilter('gameMode', 'solo')}
            >
              👤 Solo
            </button>
          </div>
        </div>

        {/* Skill Category Section */}
        <div className="filter-section">
          <h3 className="section-title">📚 Skill</h3>
          <div className="category-wrapper">
            <select
              value={filters.category || 'all'}
              onChange={(e) => updateFilter('category', e.target.value)}
              className="category-select-full"
            >
              {categories.map(category => (
                <option key={category.key} value={category.key}>
                  {category.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Musical Element Category Section */}
        <div className="filter-section">
          <h3 className="section-title">🎵 Musical Element</h3>
          <div className="category-wrapper">
            <select
              value={filters.elementCategory || 'all'}
              onChange={(e) => updateFilter('elementCategory', e.target.value)}
              className="category-select-full"
            >
              {elementCategories.map(category => (
                <option key={category.key} value={category.key}>
                  {category.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Search Section */}
        <div className="filter-section">
          <h3 className="section-title">🔍 Search</h3>
          <div className="search-wrapper">
            <input
              type="text"
              placeholder="Search for games..."
              value={filters.searchTerm}
              onChange={(e) => updateFilter('searchTerm', e.target.value)}
              className="search-input-full"
            />
          </div>
        </div>
      </div>

      {/* Musical Elements Section */}
      <div className="filter-section">
        <h3 className="section-title">🎵 Musical Elements</h3>
        <div className="pill-grid">
          {musicalElements.map(element => (
            <button
              key={element.key}
              className={`filter-pill element-pill ${filters.musicalElements.includes(element.key) ? 'selected' : ''}`}
              onClick={() => toggleArrayFilter('musicalElements', element.key)}
            >
              <span className="pill-icon">{element.icon}</span>
              <span className="pill-label">{element.label}</span>
              {filters.musicalElements.includes(element.key) && (
                <span className="pill-check">✓</span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Musical Skills Section */}
      <div className="filter-section">
        <h3 className="section-title">🎭 Musical Skills</h3>
        <div className="pill-grid">
          {musicalSkills.map(skill => (
            <button
              key={skill.key}
              className={`filter-pill skill-pill ${filters.musicalSkills.includes(skill.key) ? 'selected' : ''}`}
              onClick={() => toggleArrayFilter('musicalSkills', skill.key)}
            >
              <span className="pill-icon">{skill.icon}</span>
              <span className="pill-label">{skill.label}</span>
              {filters.musicalSkills.includes(skill.key) && (
                <span className="pill-check">✓</span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Active Filters Summary */}
      {(filters.musicalElements.length > 0 || filters.musicalSkills.length > 0) && (
        <div className="filter-section active-filters-section">
          <h3 className="section-title">✨ Active Filters</h3>
          <div className="active-filters">
            {filters.musicalElements.map(element => {
              const elementData = musicalElements.find(e => e.key === element);
              return (
                <span 
                  key={element} 
                  className="active-filter element-active"
                  onClick={() => toggleArrayFilter('musicalElements', element)}
                >
                  {elementData?.icon} {elementData?.label}
                  <span className="remove-filter">×</span>
                </span>
              );
            })}
            {filters.musicalSkills.map(skill => {
              const skillData = musicalSkills.find(s => s.key === skill);
              return (
                <span 
                  key={skill} 
                  className="active-filter skill-active"
                  onClick={() => toggleArrayFilter('musicalSkills', skill)}
                >
                  {skillData?.icon} {skillData?.label}
                  <span className="remove-filter">×</span>
                </span>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default FilterBar;
